class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Countdown shown at the top of the cart drawer. The deadline is stored so the timer
// keeps running across page loads and drawer re-renders instead of restarting each time.
class CartDrawerTimer extends HTMLElement {
  static storageKey = 'cart-drawer-timer-deadline';

  connectedCallback() {
    this.duration = parseInt(this.dataset.duration, 10) * 1000;
    if (!this.duration) return;

    this.deadline = this.getStoredDeadline();
    this.tick();
    this.interval = setInterval(this.tick.bind(this), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  getStoredDeadline() {
    try {
      return parseInt(localStorage.getItem(CartDrawerTimer.storageKey), 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  setStoredDeadline(deadline) {
    try {
      localStorage.setItem(CartDrawerTimer.storageKey, deadline);
    } catch (e) {}
  }

  tick() {
    const now = Date.now();

    // Start a new window when the previous one expired or the duration setting was shortened
    if (this.deadline <= now || this.deadline - now > this.duration) {
      this.deadline = now + this.duration;
      this.setStoredDeadline(this.deadline);
    }

    const seconds = Math.ceil((this.deadline - now) / 1000);
    const pad = (value) => String(value).padStart(2, '0');
    this.textContent = `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
  }
}

customElements.define('cart-drawer-timer', CartDrawerTimer);

// Order bump toggle. Switching it on submits the product form inside it (see product-form.js);
// switching it off removes the order bump line. Both re-render the drawer with the real state.
class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.cart-drawer-upsell__toggle');
    this.toggle.addEventListener('click', this.onToggleClick.bind(this));

    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (String(event.productVariantId) === this.dataset.variantId) this.setSelected(false);
    });
  }

  disconnectedCallback() {
    if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
  }

  onToggleClick(event) {
    if (this.toggle.getAttribute('aria-disabled') === 'true') return;

    const wasSelected = this.dataset.selected === 'true';
    this.setSelected(!wasSelected);

    if (wasSelected) {
      this.toggle.setAttribute('aria-disabled', true);
      document.querySelector('cart-drawer-items')?.updateQuantity(this.toggle.dataset.line, 0, event);
    }
  }

  setSelected(selected) {
    this.dataset.selected = selected;
    this.toggle.setAttribute('aria-checked', selected);
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
